import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

interface AdminAuthGuardProps {
  children: React.ReactNode;
}

const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL;
const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD;

// Função para criar hash simples (não é super seguro, mas funciona para o propósito)
const createHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
};

const ADMIN_TOKEN = createHash(ADMIN_EMAIL + ADMIN_PASSWORD);

export const AdminAuthGuard: React.FC<AdminAuthGuardProps> = ({ children }) => {
  const { theme } = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Verificar se há autenticação salva no localStorage (persiste entre sessões)
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken === ADMIN_TOKEN) {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Verificar credenciais
    if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      // Salvar token no localStorage para persistir entre sessões
      localStorage.setItem('adminToken', ADMIN_TOKEN);
    } else {
      setError('Credenciais inválidas');
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress aria-label="Carregando" sx={{ color: theme.colors.primary }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          px: 3,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: '100%',
            p: 4,
            backgroundColor: theme.colors.card,
            borderRadius: theme.radii.medium,
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockIcon sx={{ fontSize: 64, color: theme.colors.primary, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1 }}>
              Painel Administrativo
            </Typography>
            <Typography sx={{ color: theme.colors.text.secondary }}>
              Insira suas credenciais para continuar
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                autoFocus
              />
              <TextField
                label="Senha"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: 'white',
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    backgroundColor: theme.colors.primary,
                    opacity: 0.9,
                  },
                }}
              >
                Entrar
              </Button>
            </Box>
          </form>
        </Card>
      </Box>
    );
  }

  return <>{children}</>;
};
